import { Link } from 'react-router-dom';

import { Estimator } from '../components/Estimator';
import { Card, EmptyState, PageHeader } from '../ui';

/**
 * The front door, and a VISITOR'S page only.
 *
 * It is the one page that explains what Nexus is before somebody is asked to
 * sign in. There is no signed-in version of it any more: a creator's home is
 * Campaigns and an operator's is the Overview, the sidebar goes straight to
 * both, and a "welcome back" page in between was a second place for figures
 * that already live on the screens that own them. `/` redirects to whichever
 * home is yours the moment there is a session (see `SignedOutOnly`).
 */
export function Home(): React.JSX.Element {
  return (
    <>
      <PageHeader
        hero
        eyebrow="Clipping campaigns"
        title={
          <>
            Get paid for the clips <span className="gradient-text">you already cut</span>
          </>
        }
        lead="A client funds a campaign and points you at their source material. You cut the clips, post them on your own accounts, and Nexus pays you per thousand verified views."
        actions={
          <>
            <Link className="btn btn-primary" to="/signup">
              Create an account
            </Link>
            <Link className="btn btn-ghost" to="/login">
              Sign in
            </Link>
          </>
        }
      />

      {/**
       * THE CALCULATOR, on the front door.
       *
       * "Paid per thousand verified views" is a sentence a visitor cannot price.
       * Every platform in this market puts a calculator on its landing page for
       * that reason, and the reason is a good one — but ours has to stay honest
       * about the two things those pages tend to leave out: the rate is the
       * campaign's, not the site's, and the views are the verified ones.
       */}
      <Card title="What would that be worth?" variant="feature">
        <Estimator ratePerThousandCents={100} editableRate />
      </Card>

      <div className="split">
        <Card title="How it works" variant="feature">
          <ol className="rows steps">
            <li>
              <span className="row-title">Prove the accounts you post from</span>
              <span className="muted">
                A short code in your bio, or a read-only sign-in with the platform. Nexus never
                posts as you.
              </span>
            </li>
            <li>
              <span className="row-title">Join a campaign and read its rules</span>
              <span className="muted">
                The rate, the budget left, what to cut from, and what a clip must and must not
                contain — before you spend an evening editing.
              </span>
            </li>
            <li>
              <span className="row-title">Post the clip and paste the link</span>
              <span className="muted">
                Automatic checks confirm the post is public, yours, in the window and not a
                duplicate. An operator checks the content.
              </span>
            </li>
            <li>
              <span className="row-title">Get paid on verified views</span>
              <span className="muted">
                Views are read at 6, 24 and 72 hours. Earnings clear a short holdback and are paid
                in a weekly batch.
              </span>
            </li>
          </ol>
        </Card>

        <Card title="What Nexus does not do">
          <EmptyState title="It never touches your accounts.">
            Nexus reads public statistics and, where you sign in with a platform, a read-only
            scope. It never posts, never messages, and never asks for a password to an account
            that is not ours.
          </EmptyState>
          <p className="muted">
            Signing up takes a minute: an email address, a name, a password and the terms.{' '}
            <Link to="/signup">Create an account</Link>.
          </p>
        </Card>
      </div>
    </>
  );
}
